import * as uniqBy from 'lodash/uniqBy'
import { State } from 'interfaces/state'
import { Guards } from 'interfaces/guards'
import { connectLoadable, createParallelGuard } from 'helpers/loadableHelpers'
import { peergroupsGuard, selectedPeergroupTotalsGuard } from 'guards/globalGuards'
import { createSelectedTotalsByTypeSelector, selectedRelationSelector } from 'selectors/peergroupSelectors'
import { RankChart, LineChartInnerOwnProps, LineChartInnerStateProps, Tooltip, TooltipDirection } from 'components/features/pfm/LineChart/LineChart'
import LineChartLoadingOverlay from 'components/atoms/preloaders/LineChartLoadingOverlay'
import { createSelector } from 'reselect'
import { ALL_CATEGORY_ID } from 'constants/meta'
import { findRank, RANK_CHART_WIDTH, RANK_CHART_DIVIDERS, RANK_CHART_TIPS, RANK_CHART_SECTIONS, percentileToItem } from 'helpers/trendHelpers'
import { createOwnPropsSelector } from 'selectors/globalSelectors'
import { memberAccountTrendsGuard } from 'guards/accountGuards'
import { getMonthsCount } from 'helpers/dateTimeHelpers'
import { memberAccountRankCalculationValueSelector } from 'selectors/accountSelectors'
import { AccountPage } from 'interfaces/pages'
import { Timeframe } from 'interfaces/chart'
import { CategoryId } from 'interfaces/meta'
import { formatRank, formatUSCurrency } from 'helpers/formatHelpers'
import { RANK_CHART_SCALES } from 'constants/chart'
import { createForgottenGuardError } from 'helpers/errorHelpers'
import { isMobileSelector } from 'selectors/viewportSelectors'
import { getTimeframeCopy } from 'helpers/categoriesHelpers'
import { withComparisonSelector } from 'components/features/pageFilters/selectors'

export type AccountRankChartOwnProps = {
  type: AccountPage
  timeframe: Timeframe
  categoryId: CategoryId
}

type OwnProps =
  & AccountRankChartOwnProps
  & LineChartInnerOwnProps

const createMapStateToProps = () => {
  const selector = createSelector(
    createSelectedTotalsByTypeSelector(),
    memberAccountRankCalculationValueSelector,
    selectedRelationSelector,
    isMobileSelector,
    createOwnPropsSelector<OwnProps>(),
    withComparisonSelector,
    (totals, decimalRankCalculationValue, selectedRelation, isMobile, ownProps, withComparison): LineChartInnerStateProps => {
      if (!totals) {
        throw createForgottenGuardError('AccountsRankChartContainer', 'selectedPeergroupTotalsGuard')
      }

      if (!selectedRelation) {
        throw createForgottenGuardError('AccountsRankChartContainer', 'peergroupsGuard')
      }

      const { peerValue: peerTotals, otherValue: otherTotals } = totals
      const { type, timeframe } = ownProps
      const rankCalculationValue = Math.round(decimalRankCalculationValue)
      const peersRank = findRank(peerTotals.percentiles, rankCalculationValue)
      const othersRank = findRank(otherTotals.percentiles, rankCalculationValue)

      const w1 = getMonthsCount(timeframe) === 1 ? 'YOUR' : 'YOUR AVG.'
      const w2 = type === 'assets' ? 'ASSETS' : 'DEBTS'
      const lineTitle = `${ w1 } ${ w2 }`

      // <= is important because when they squash to one that one should be up
      const peersTooltipDirection: TooltipDirection = peersRank <= othersRank ? 'up' : 'down'
      const othersTooltipDirection: TooltipDirection = peersTooltipDirection === 'up' ? 'down' : 'up'
      // squash tooltips if they have same amount
      const defaultTooltips = uniqBy<Tooltip>(
        [
          {
            type: 'peers',
            text: formatRank(peersRank),
            x: peersRank,
            y: rankCalculationValue,
            direction: peersTooltipDirection,
          },
          {
            type: 'others',
            text: formatRank(othersRank),
            x: othersRank,
            y: rankCalculationValue,
            direction: othersTooltipDirection,
          },
        ],
        (tooltip) => tooltip.x
      )


      return {
        memberItems: [],
        peersItems: peerTotals.percentiles.map(percentileToItem),
        otherItems: otherTotals.percentiles.map(percentileToItem),

        dots: [
          {
            x: peersRank,
            y: rankCalculationValue,
            type: 'peers',
            tooltipType: 'peers',
          },
          {
            x: othersRank,
            y: rankCalculationValue,
            type: 'others',
            tooltipType: 'others',
          },
        ],

        limitLine: {
          title: lineTitle,
          amount: rankCalculationValue,
          kind: 'member',
        },

        defaultTooltips,

        xWidth: RANK_CHART_WIDTH,

        dividers: RANK_CHART_DIVIDERS,
        headerTips: getTimeframeCopy(timeframe),
        tips: RANK_CHART_TIPS,
        sections: RANK_CHART_SECTIONS,

        scales: RANK_CHART_SCALES,
        valueDecorator: formatUSCurrency,

        selectedPeergroupName: selectedRelation.name,
        isMobile,
        withComparison,
      }
    }
  )

  return (state: State, ownProps: OwnProps): LineChartInnerStateProps => {
    const { categoryId } = ownProps

    /**
     * A back end doesn't support percentiles for categories
     * therefore we just push fake data to the control and don't show it (see `TrendRankBadge`)
     * But this will be supported so we'll wait. 🙏
     */
    if (categoryId !== ALL_CATEGORY_ID) {
      return null as any
    }

    return selector(state, ownProps)
  }
}

const trendRankBadgeGuards: Guards<OwnProps> = [
  peergroupsGuard,
  createParallelGuard(
    selectedPeergroupTotalsGuard,
    memberAccountTrendsGuard
  ),
]

const connected = connectLoadable<
  LineChartInnerStateProps,
  {},
  LineChartInnerOwnProps,
  OwnProps
>(trendRankBadgeGuards, LineChartLoadingOverlay)(createMapStateToProps)(RankChart)
export default connected
